import React, { useEffect, useState } from 'react';
import { useSources } from '../../contexts/SourcesContext';
import { useAuth } from '../../hooks/useAuth';
import { SourceForm } from './SourceForm';
import apiService from '../../services/api';

export function SourcesTab() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const {
    sources,
    loading,
    error,
    showAddSource,
    editingSource,
    setShowAddSource,
    setEditingSource,
    fetchSources,
    createSource,
    updateSource,
    toggleSource,
    deleteSource,
    triggerScraping
  } = useSources();

  // Bulk selection state
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<number>>(new Set());
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  const [validatingFeeds, setValidatingFeeds] = useState<Set<number>>(new Set());
  const [feedValidationResults, setFeedValidationResults] = useState<{
    [key: number]: { valid: boolean; error?: string; timestamp: number }
  }>({});

  // Scraping operation state
  const [scrapingOperations, setScrapingOperations] = useState<{
    [key: string]: { status: 'idle' | 'scraping' | 'success' | 'error'; message?: string }
  }>({});
  
  const [lastScrapingResult, setLastScrapingResult] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Deletion operation state
  const [deletingSourceId, setDeletingSourceId] = useState<number | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    sourceId: number;
    sourceName: string;
  } | null>(null);

  // Fetch sources when component mounts
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchSources();
    }
  }, [isAuthenticated, authLoading, fetchSources]);

  // Helper functions for bulk operations
  const isAllSelected = sources.length > 0 && selectedSourceIds.size === sources.length;
  const hasSelection = selectedSourceIds.size > 0;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedSourceIds(new Set());
    } else {
      setSelectedSourceIds(new Set(sources.map(s => s.id)));
    }
  };

  const handleSelectSource = (sourceId: number) => {
    const newSelected = new Set(selectedSourceIds);
    if (newSelected.has(sourceId)) {
      newSelected.delete(sourceId);
    } else {
      newSelected.add(sourceId);
    }
    setSelectedSourceIds(newSelected);
  };

  const handleBulkPause = async () => {
    if (!hasSelection || bulkOperationInProgress) return;
    
    setBulkOperationInProgress(true);
    try {
      const selectedSources = sources.filter(s => selectedSourceIds.has(s.id) && s.isActive);
      
      for (const source of selectedSources) {
        await toggleSource(source.id);
      }
      
      setLastScrapingResult({
        type: 'success',
        message: `Paused ${selectedSources.length} sources`
      });
      setSelectedSourceIds(new Set());
    } catch (error) {
      setLastScrapingResult({
        type: 'error',
        message: `Failed to pause sources: ${(error as Error).message}`
      });
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const handleBulkActivate = async () => {
    if (!hasSelection || bulkOperationInProgress) return;
    
    setBulkOperationInProgress(true);
    try {
      const selectedSources = sources.filter(s => selectedSourceIds.has(s.id) && !s.isActive);
      
      for (const source of selectedSources) {
        await toggleSource(source.id);
      }
      
      setLastScrapingResult({
        type: 'success',
        message: `Activated ${selectedSources.length} sources`
      });
      setSelectedSourceIds(new Set());
    } catch (error) {
      setLastScrapingResult({
        type: 'error',
        message: `Failed to activate sources: ${(error as Error).message}`
      });
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const handleBulkToggleAutoProcess = async (enable: boolean) => {
    if (!hasSelection || bulkOperationInProgress) return;
    
    setBulkOperationInProgress(true);
    try {
      const selectedSources = sources.filter(s => selectedSourceIds.has(s.id));
      
      for (const source of selectedSources) {
        // Only send the fields that should be updated
        await updateSource(source.id, { 
          name: source.name,
          url: source.url,
          sourceType: source.sourceType,
          language: source.language,
          reliabilityScore: source.reliabilityScore,
          biasScore: source.biasScore,
          targetCountries: source.targetCountries,
          isActive: source.isActive,
          relevanceThreshold: source.relevanceThreshold,
          autoProcess: enable 
        });
      }
      
      setLastScrapingResult({
        type: 'success',
        message: `Updated auto-process for ${selectedSources.length} sources`
      });
      setSelectedSourceIds(new Set());
    } catch (error) {
      setLastScrapingResult({
        type: 'error',
        message: `Failed to update sources: ${(error as Error).message}`
      });
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!hasSelection || bulkOperationInProgress) return;
    
    const selectedSources = sources.filter(s => selectedSourceIds.has(s.id));
    const confirmMessage = `Are you sure you want to delete ${selectedSources.length} news sources? This will permanently delete all their articles and analyses.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    setBulkOperationInProgress(true);
    try {
      let deletedCount = 0;
      
      for (const source of selectedSources) {
        await deleteSource(source.id);
        deletedCount++;
      }
      
      setLastScrapingResult({
        type: 'success',
        message: `Deleted ${deletedCount} sources`
      });
      setSelectedSourceIds(new Set());
    } catch (error) {
      setLastScrapingResult({
        type: 'error',
        message: `Failed to delete sources: ${(error as Error).message}`
      });
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const handleValidateFeed = async (sourceId: number) => {
    const source = sources.find(s => s.id === sourceId);
    if (!source) return;
    
    setValidatingFeeds(prev => new Set(prev).add(sourceId));
    
    try {
      const result = await apiService.validateRSSFeed(source.url);
      
      setFeedValidationResults(prev => ({
        ...prev,
        [sourceId]: {
          valid: result.valid,
          error: result.error,
          timestamp: Date.now()
        }
      }));
    } catch (error) {
      setFeedValidationResults(prev => ({
        ...prev,
        [sourceId]: {
          valid: false,
          error: 'Failed to validate feed',
          timestamp: Date.now()
        }
      }));
    } finally {
      setValidatingFeeds(prev => {
        const newSet = new Set(prev);
        newSet.delete(sourceId);
        return newSet;
      });
    }
  };


  const handleSaveSource = async (sourceData: any) => {
    try {
      if (editingSource) {
        await updateSource(editingSource.id, sourceData);
      } else {
        await createSource(sourceData);
      }
    } catch (error) {
      alert('Failed to save source');
    }
  };

  const handleToggleSource = async (sourceId: number) => {
    try {
      await toggleSource(sourceId);
    } catch (error) {
      alert('Failed to toggle source');
    }
  };

  const handleTriggerScraping = async (sourceId?: number) => {
    const operationKey = sourceId ? `source-${sourceId}` : 'all-sources';
    const sourceName = sourceId ? sources.find(s => s.id === sourceId)?.name || 'Unknown' : 'All Sources';
    
    try {
      // Set scraping state
      setScrapingOperations(prev => ({
        ...prev,
        [operationKey]: { status: 'scraping' }
      }));
      setLastScrapingResult(null);

      await triggerScraping(sourceId);
      
      // Success state
      setScrapingOperations(prev => ({
        ...prev,
        [operationKey]: { status: 'success' }
      }));
      setLastScrapingResult({
        type: 'success',
        message: `Scraping triggered successfully for ${sourceName}`
      });

      // Clear success state after 3 seconds
      setTimeout(() => {
        setScrapingOperations(prev => ({
          ...prev,
          [operationKey]: { status: 'idle' }
        }));
      }, 3000);

    } catch (error) {
      // Error state
      setScrapingOperations(prev => ({
        ...prev,
        [operationKey]: { status: 'error', message: (error as Error).message }
      }));
      setLastScrapingResult({
        type: 'error',
        message: `Failed to trigger scraping for ${sourceName}: ${(error as Error).message}`
      });

      // Clear error state after 5 seconds
      setTimeout(() => {
        setScrapingOperations(prev => ({
          ...prev,
          [operationKey]: { status: 'idle' }
        }));
      }, 5000);
    }
  };

  const handleDeleteSource = async (sourceId: number) => {
    try {
      setDeletingSourceId(sourceId);
      const result = await deleteSource(sourceId);
      
      setLastScrapingResult({
        type: 'success',
        message: result.message
      });
      
      setDeleteConfirmation(null);
    } catch (error) {
      setLastScrapingResult({
        type: 'error',
        message: `Failed to delete news source: ${(error as Error).message}`
      });
    } finally {
      setDeletingSourceId(null);
    }
  };

  const confirmDelete = (sourceId: number, sourceName: string) => {
    setDeleteConfirmation({ sourceId, sourceName });
  };

  if (error) {
    return (
      <div className="tc-card p-6">
        <div className="text-center text-red-600">
          <p>Error: {error}</p>
          <button onClick={fetchSources} className="tc-btn tc-btn-primary mt-4">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sources Management */}
      <div className="tc-card p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold tc-text-primary">News Sources</h3>
          <div className="space-x-2">
            <button
              onClick={() => handleTriggerScraping()}
              disabled={scrapingOperations['all-sources']?.status === 'scraping'}
              className={`tc-btn tc-btn-secondary ${
                scrapingOperations['all-sources']?.status === 'scraping' ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {scrapingOperations['all-sources']?.status === 'scraping' ? (
                <>
                  <div className="tc-spinner w-4 h-4 mr-2"></div>
                  Scraping...
                </>
              ) : scrapingOperations['all-sources']?.status === 'success' ? (
                <>✅ Triggered All</>
              ) : scrapingOperations['all-sources']?.status === 'error' ? (
                <>❌ Failed</>
              ) : (
                <>🔄 Trigger All Scraping</>
              )}
            </button>
            <button
              onClick={() => setShowAddSource(true)}
              className="tc-btn tc-btn-primary"
            >
              ➕ Add Source
            </button>
          </div>
        </div>

        {/* Bulk operations toolbar */}
        {hasSelection && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center justify-between">
              <p className="text-sm text-blue-800">
                {selectedSourceIds.size} source{selectedSourceIds.size !== 1 ? 's' : ''} selected
              </p>
              <div className="space-x-2">
                <button
                  onClick={handleBulkPause}
                  disabled={bulkOperationInProgress}
                  className="tc-btn tc-btn-sm tc-btn-secondary"
                >
                  ⏸️ Pause Selected
                </button>
                <button
                  onClick={handleBulkActivate}
                  disabled={bulkOperationInProgress}
                  className="tc-btn tc-btn-sm tc-btn-secondary"
                >
                  ▶️ Activate Selected
                </button>
                <button
                  onClick={() => handleBulkToggleAutoProcess(true)}
                  disabled={bulkOperationInProgress}
                  className="tc-btn tc-btn-sm tc-btn-secondary"
                >
                  ✅ Enable Auto-Process
                </button>
                <button
                  onClick={() => handleBulkToggleAutoProcess(false)}
                  disabled={bulkOperationInProgress}
                  className="tc-btn tc-btn-sm tc-btn-secondary"
                >
                  ⏸️ Disable Auto-Process
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkOperationInProgress}
                  className="tc-btn tc-btn-sm bg-red-600 hover:bg-red-700 text-white"
                >
                  🗑️ Delete Selected
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scraping feedback */}
        {lastScrapingResult && (
          <div className={`mb-4 p-3 rounded-md border ${
            lastScrapingResult.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <p className="text-sm">
              {lastScrapingResult.type === 'success' ? '✅' : '❌'} {lastScrapingResult.message}
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <p className="tc-text-secondary">Loading sources...</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full divide-y divide-gray-200">
              <thead className="tc-bg-secondary">
                <tr>
                  <th className="pl-6 pr-3 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                    Reliability
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                    Auto Process
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium tc-text-secondary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="tc-bg-primary divide-y divide-gray-200">
                {sources.map((source) => {
                  const validationResult = feedValidationResults[source.id];
                  const isValidating = validatingFeeds.has(source.id);
                  
                  return (
                  <tr key={source.id} className="tc-hover">
                    <td className="pl-6 pr-3 py-4">
                      <input
                        type="checkbox"
                        checked={selectedSourceIds.has(source.id)}
                        onChange={() => handleSelectSource(source.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 min-w-[400px]">
                      <div>
                        <div className="text-sm font-medium tc-text-primary mb-1">{source.name}</div>
                        <div className="flex items-start space-x-2">
                          <div className="flex-1">
                            <div className={`text-xs tc-text-secondary break-all ${
                              validationResult && !validationResult.valid ? 'text-red-600' : ''
                            }`}>
                              {source.url}
                            </div>
                            {validationResult && !validationResult.valid && (
                              <div className="text-xs text-red-500 mt-1">
                                {validationResult.error}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleValidateFeed(source.id)}
                            disabled={isValidating}
                            className={`flex-shrink-0 text-xs p-1 rounded hover:bg-gray-100 ${
                              isValidating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            title="Validate RSS Feed"
                          >
                            {isValidating ? (
                              <span className="animate-spin">⏳</span>
                            ) : validationResult ? (
                              validationResult.valid ? '✅' : '❌'
                            ) : (
                              '🔍'
                            )}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm tc-text-secondary">
                      {source.sourceType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm tc-text-secondary">
                      {source.reliabilityScore}/10
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        source.autoProcess 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {source.autoProcess ? '✅ Auto' : '⏸️ Manual'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        source.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {source.isActive ? '✅ Active' : '❌ Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <button
                          onClick={() => setEditingSource(source)}
                          className="tc-link p-1 hover:bg-gray-100 rounded"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleToggleSource(source.id)}
                          className="tc-link p-1 hover:bg-gray-100 rounded"
                          title={source.isActive ? 'Pause' : 'Activate'}
                        >
                          {source.isActive ? '⏸️' : '▶️'}
                        </button>
                        <button
                          onClick={() => handleTriggerScraping(source.id)}
                          disabled={scrapingOperations[`source-${source.id}`]?.status === 'scraping'}
                          className={`tc-link p-1 hover:bg-gray-100 rounded ${
                            scrapingOperations[`source-${source.id}`]?.status === 'scraping' ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          title="Scrape Now"
                        >
                          {scrapingOperations[`source-${source.id}`]?.status === 'scraping' ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            '🔄'
                          )}
                        </button>
                        <button
                          onClick={() => confirmDelete(source.id, source.name)}
                          disabled={deletingSourceId === source.id}
                          className={`tc-link p-1 hover:bg-red-100 rounded text-red-600 hover:text-red-800 ${
                            deletingSourceId === source.id ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          title="Delete"
                        >
                          {deletingSourceId === source.id ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            '🗑️'
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Source Form Modal */}
      {(showAddSource || editingSource) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="tc-card p-6 max-w-4xl max-h-[90vh] overflow-auto">
            <SourceForm
              source={editingSource}
              onSave={handleSaveSource}
              onCancel={() => {
                setShowAddSource(false);
                setEditingSource(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="tc-card p-6 max-w-md">
            <h3 className="text-lg font-semibold tc-text-primary mb-4">
              Confirm Deletion
            </h3>
            <p className="tc-text-secondary mb-6">
              Are you sure you want to delete the news source <strong>"{deleteConfirmation.sourceName}"</strong>?
            </p>
            <p className="tc-text-secondary mb-6 text-sm bg-red-50 border border-red-200 rounded p-3">
              <strong>⚠️ Warning:</strong> This will permanently delete the news source and all its related articles, analyses, and data. This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="tc-btn tc-btn-secondary"
                disabled={deletingSourceId !== null}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSource(deleteConfirmation.sourceId)}
                disabled={deletingSourceId !== null}
                className="tc-btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                {deletingSourceId === deleteConfirmation.sourceId ? (
                  <>
                    <div className="tc-spinner w-4 h-4 mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Permanently'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}